import React from 'react';
import styled from 'styled-components';
import ImageUpload from '../ImageUpload';
import { useUser } from '../../context/UserContext';
import axios from 'axios';
import { CloseOutlined } from '@ant-design/icons';
import Message from '../Message';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const MessageContainer = styled.div.attrs({ id: 'avatar-modal-message' })`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  pointer-events: none;
  display: flex;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 320px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: none;
  margin: 16px;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 480px) {
    padding: 20px;
    margin: 12px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  font-size: 18px;
  transition: all 0.3s ease;
  border-radius: 50%;
  
  &:hover {
    opacity: 0.6;
    background: rgba(0, 0, 0, 0.08);
  }
`;

const Title = styled.h2`
  text-align: left;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.text};
  font-size: 16px;
  font-weight: 500;
  width: 100%;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  width: 100%;
`;

const Button = styled.button`
  flex: 1;
  height: 36px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${({ primary }) => primary ? `
    background: #FF4D4F;
    color: white;
    
    &:hover {
      background: #E53E3E;
    }

    &:active {
      background: #d9363e;
    }
  ` : `
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5'};
    color: ${({ theme }) => theme.text};
    
    &:hover {
      background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#e0e0e0'};
    }

    &:active {
      background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#d9d9d9'};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const UpdateAvatarModal = ({ onClose }) => {
  const { user, updateUserAndToken } = useUser();

  const showMessage = (content, type) => {
    Message[type](content, 'avatar-modal-message');
  };

  const handleUploadAvatar = async (imageData) => {
    try {
      const formData = new FormData();
      
      if (typeof imageData === 'string' && imageData.startsWith('data:')) {
        const arr = imageData.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        const file = new File([u8arr], 'avatar.jpg', { type: mime });
        formData.append('upfile', file);
      } else if (imageData instanceof Blob) {
        const file = new File([imageData], 'avatar.jpg', { type: 'image/jpeg' });
        formData.append('upfile', file);
      } else {
        formData.append('upfile', imageData);
      }

      showMessage('上传中...', 'info');
      
      const response = await axios.post(
        '//server.battleverse.cn/user/pic/upload.do',
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success || response.data.code === 0) {
        const { user: updatedUser, token } = response.data.data;
        
        if (token) {
          localStorage.setItem('token', token);
        }
        
        updateUserAndToken(updatedUser, token);
        
        localStorage.setItem('userInfo', JSON.stringify(updatedUser));
        
        showMessage('头像更新成功', 'success');
        
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 1000);
        
        return {
          success: true,
          data: {
            avatarUrl: updatedUser.userPic,
            token,
            user: updatedUser
          }
        };
      } else {
        throw new Error(response.data.msg || '上传失败');
      }
    } catch (error) {
      console.error('上传头像失败:', error);
      showMessage(error.message || '上传失败，请重试', 'error');
      return {
        success: false,
        message: error.response?.data?.msg || '上传失败，请重试'
      };
    }
  };

  return (
    <ModalOverlay>
      <MessageContainer />
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <CloseOutlined />
        </CloseButton>
        <Title>修改头像</Title>
        <ImageUpload
          currentImage={user?.userPic}
          onUpload={handleUploadAvatar}
          onClose={onClose}
        />
      </ModalContent>
    </ModalOverlay>
  );
};

export default UpdateAvatarModal;
